import React, { forwardRef, useEffect, useRef, useState } from 'react';
import './TvVideoPlayer.css';

const SEEK_STEP_SECONDS = 10;
// Remote keys arrive as DOM keys: D-pad → Arrow*, centre → Enter, plus the media keys.
const TOGGLE_KEYS = new Set(['Enter', ' ', 'MediaPlayPause', 'MediaPlay', 'MediaPause']);
const SEEK_BACK_KEYS = new Set(['ArrowLeft', 'MediaRewind']);
const SEEK_FORWARD_KEYS = new Set(['ArrowRight', 'MediaFastForward']);

interface TvVideoPlayerProps {
  src: string;
  onError: () => void;
}

interface PlaybackProgress {
  elapsed: number;
  duration: number;
}

function seekBy(video: HTMLVideoElement, deltaSeconds: number): void {
  const target = video.currentTime + deltaSeconds;
  const upperBound = video.duration || target;
  video.currentTime = Math.min(Math.max(target, 0), upperBound);
}

function togglePlayback(video: HTMLVideoElement): void {
  if (video.paused) {
    void video.play();
  } else {
    video.pause();
  }
}

/**
 * The TV's full-screen video: native controls are unreachable from the remote, so the
 * player takes the keys itself and draws a progress bar. Handled keys call preventDefault,
 * so spatial navigation never moves focus out of the video.
 */
const TvVideoPlayer = forwardRef<HTMLVideoElement, TvVideoPlayerProps>(({ src, onError }, forwardedRef) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState<PlaybackProgress>({ elapsed: 0, duration: 0 });

  const setVideoRef = (node: HTMLVideoElement | null) => {
    videoRef.current = node;
    if (typeof forwardedRef === 'function') {
      forwardedRef(node);
    } else if (forwardedRef) {
      forwardedRef.current = node;
    }
  };

  // One screen wake lock per play span, so the screensaver cannot interrupt a video but can
  // start once it stops. `playing` refires after every seek or stall, but an unchanged
  // isPlaying does not rerun this effect. The cleanup also releases a request that resolves
  // after the span ended: a leaked sentinel would hold the screen on for as long as the app is open.
  useEffect(() => {
    if (!isPlaying) {
      return;
    }

    let heldSentinel: WakeLockSentinel | null = null;
    let isSpanOver = false;
    navigator.wakeLock?.request('screen')
      .then((sentinel) => {
        if (isSpanOver) {
          void sentinel.release();
          return;
        }

        heldSentinel = sentinel;
      })
      .catch(() => undefined);

    return () => {
      isSpanOver = true;
      void heldSentinel?.release();
    };
  }, [isPlaying]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLVideoElement>) => {
    const video = event.currentTarget;
    if (TOGGLE_KEYS.has(event.key)) {
      event.preventDefault();
      togglePlayback(video);
      return;
    }

    if (SEEK_BACK_KEYS.has(event.key)) {
      event.preventDefault();
      seekBy(video, -SEEK_STEP_SECONDS);
      return;
    }

    if (SEEK_FORWARD_KEYS.has(event.key)) {
      event.preventDefault();
      seekBy(video, SEEK_STEP_SECONDS);
    }
  };

  const syncProgress = (video: HTMLVideoElement) => {
    const knownDuration = video.duration || 0;
    setProgress({ elapsed: video.currentTime, duration: knownDuration });
  };

  return (
    <div className="tv-video">
      <video
        ref={setVideoRef}
        src={src}
        autoPlay
        playsInline
        tabIndex={0}
        className="video-modal-video tv-video__media"
        aria-label="Video content"
        onKeyDown={handleKeyDown}
        onError={onError}
        onPlaying={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
        onTimeUpdate={(event) => syncProgress(event.currentTarget)}
        // Amazon WebView reports a placeholder duration until playback starts (web-app FAQ).
        onDurationChange={(event) => syncProgress(event.currentTarget)}
      />
      <div className="tv-video__bar" aria-hidden="true">
        <span className="tv-video__state">{isPlaying ? '▶' : '⏸'}</span>
        <progress className="tv-video__progress" max={progress.duration || 1} value={progress.elapsed} />
      </div>
    </div>
  );
});

TvVideoPlayer.displayName = 'TvVideoPlayer';

export default TvVideoPlayer;
